import { useCallback, useState } from 'react';
import { Modal } from 'react-bootstrap';
import type { FunctionComponent } from 'react';

import { isCustomLicense } from '../../../../shared/licenses';
import { useDialogContext } from '../../../contexts/DialogContext';
import { useInputValue } from '../../../hooks';
import Button, { ButtonGroup } from '../../generic/Button';
import { FormGroup, LabeledTextArea, LabeledTextInput } from '../../generic/Forms';
import { SupportLink } from '../../generic/Link';
import Text from '../../generic/Text';

import LicenseSelector from './LicenseSelector';

export type NewLicense = {
  licenseId: string;
  licenseTitle: string;
  licenseText: string;
  copyright: string;
};

type NewModalAddLicenseProps = {
  onCancel: () => void;
  onSave: (newLicense: NewLicense) => void;
  isNested?: boolean; // If the modal is "nested" (ie: if is on top of other modals)
  shouldSkipWarning?: boolean; // Pass this to skip the "you will need to rescan" alert.
};

const NewModalAddLicense: FunctionComponent<NewModalAddLicenseProps> = ({
  onCancel,
  onSave,
  isNested,
  shouldSkipWarning,
}) => {
  const [licenseId, setLicenseId] = useState('');
  const [licenseTitle, onLicenseTitleChange] = useState('');
  const [licenseText, onLicenseTextChange] = useInputValue('');
  const [copyright, onCopyrightChange] = useInputValue('');

  const { onConfirm } = useDialogContext();

  //  Handle saving, including handling displaying the warning telling users to rescan.
  //  Note: fn returns the click handler fn with newLicense bound.
  const handleSave = useCallback((newLicense: NewLicense) => () => {
    if (!shouldSkipWarning) {
      return onConfirm({
        title: 'Policy rescan required',
        body: 'Note: In order for this change to be reflected in your issues, you will need to re-run a policy scan.',
        onConfirm: () => onSave(newLicense),
      });
    }
    return onSave(newLicense);
  }, []);

  return (
    <Modal onHide={onCancel} show backdropClassName={isNested ? 'modal-nested' : undefined}>
      <div className="modal-add-license">
        <div className="margin-bottom-md">
          <Text.H2>
            Add License
          </Text.H2>
        </div>
        <div className="margin-bottom-md">
          <LicenseSelector
            selectedLicense={licenseId}
            onSelect={setLicenseId}
          />
        </div>
        {isCustomLicense(licenseId) && (
          <div className="margin-bottom-md">
            <LabeledTextInput
              label="Custom License Name"
              placeholder="my-custom-license-name"
              value={licenseTitle}
              onChange={onLicenseTitleChange}
            />
          </div>
        )}
        <FormGroup direction="row">
          <LabeledTextArea
            label="Raw License Text"
            placeholder="License text"
            value={licenseText}
            onChange={onLicenseTextChange}
          />
          <LabeledTextArea
            label="Copyright(s)"
            placeholder="Copyright text"
            value={copyright}
            onChange={onCopyrightChange}
          />
        </FormGroup>
        <footer className="modal-add-license__footer">
          <div className="modal-add-license__footer__info">
            <div className="block">
              Can&apos;t find the license you&apos;re looking for?
            </div>
            <SupportLink>
              Contact support
            </SupportLink>
            {' '}
            to request it be added.
          </div>
          <ButtonGroup>
            <Button
              type="secondary"
              size="medium"
              onClick={onCancel}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              size="medium"
              onClick={handleSave({ licenseId, licenseTitle, licenseText, copyright })}
              isDisabled={!licenseId}
            >
              Add
            </Button>
          </ButtonGroup>
        </footer>
      </div>
    </Modal>
  );
};

export default NewModalAddLicense;
